/**
 * @author      Abdul Pathan
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Modal,
} from 'react-bootstrap';
import SparkApi from '../../../api/SparkApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment-timezone';
import { useNavigate, useLocation } from 'react-router-dom';
import { MultiSelect } from 'react-multi-select-component';
import { saveAs } from 'file-saver';
import TemplateModal from '../whatsapp_messenger/TemplateModal';
import DataApi from '../../../api/DataApi';
import WatconnectApi from '../../../api/WatconnectApi';
import ListViewFilterPanel from '../../layout/components/ListViewFilterPanel';
import { useListViewFilters } from '../../layout/hooks/useListViewFilters';
import { getCompleteListViewFilters } from '../../../utils/conditionBuilderUtils';
import { FaFilter } from 'react-icons/fa';

const CampaignAdd = ({ selectedWhatsAppSetting, userInfo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingData = location.state || '';

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [moduleConfigs, setModuleConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [listViews, setListViews] = useState([]);
  const [selectedListView, setSelectedListView] = useState(null);
  const [isLoadingListViews, setIsLoadingListViews] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const originalListViewFiltersRef = useRef([]);
  const originalListViewLogicRef = useRef('AND');

  const objectName = selectedConfig?.object_name || '';
  const listViewName = selectedListView?.name || selectedListView?.id || '';

  const {
    filterPanelOpen,
    filterPanelResourcesLoading,
    picklistOptionsByFieldId,
    externalFilters,
    externalFilterLogic,
    availableFields,
    filterValueFields,
    toggleFilterPanel,
    addExternalFilter,
    removeExternalFilter,
    updateExternalFilter,
    patchExternalFilter,
    setExternalFilters,
    setExternalFilterLogic,
    setFilterPanelOpen,
  } = useListViewFilters(objectName, listViews, listViewName);
  const [campaignRecord, setCampaignRecord] = useState({
    name: '',
    description: '',
    schedule_date: '',
    type: 'Web',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSpinner, setIsSpinner] = useState(true);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [fileMembers, setFileMembers] = useState([]);
  const [finalTotalCost, setFinalTotalCost] = useState(0);
  const [templateRates, setTemplateRates] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const fileInputRef = useRef(null);
  const [recurrenceType, setRecurrenceType] = useState('');
  const [customRecurrenceUnit, setCustomRecurrenceUnit] = useState('week');
  const [repeatEvery, setRepeatEvery] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [recurrenceEndType, setRecurrenceEndType] = useState('never');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [existingFileInfo, setExistingFileInfo] = useState(null);
  const [templateSubmittedData, setTemplateSubmittedData] = useState(null);

  const isEditCloneMode = Boolean(
    existingData && existingData?.type === 'clone'
  );

  useEffect(() => {
    fetchAllData();
  }, [selectedWhatsAppSetting]);

  useEffect(() => {
    if (isEditCloneMode && existingData) {
      setCampaignRecord({
        name: existingData?.campaign_name
          ? `${existingData.campaign_name} clone`
          : '',
        description: existingData?.file_description || '',
        schedule_date: new Date(),
        type: existingData?.campaign_type || 'Web',
      });

      if (existingData?.recurrence) {
        setRecurrenceType(existingData.recurrence);
      }
      if (existingData?.custom_recurrence_unit) {
        setCustomRecurrenceUnit(existingData.custom_recurrence_unit);
      }
      if (existingData?.selected_days) {
        let days = [];
        try {
          days = typeof existingData.selected_days === 'string'
            ? JSON.parse(existingData.selected_days)
            : existingData.selected_days;
        } catch (e) {
          days = existingData.selected_days;
        }
        setSelectedDays(Array.isArray(days) ? days : []);
      }
      if (existingData?.selected_date) {
        setSelectedDate(existingData.selected_date);
      }
      if (existingData?.recurrence_end_type) {
        setRecurrenceEndType(existingData.recurrence_end_type);
      }
      if (existingData?.recurrence_end_date) {
        setRecurrenceEndDate(existingData.recurrence_end_date);
      }

      if (existingData?.template_name) {
        const tmplObj = { name: existingData.template_name };
        setSelectedTemplate(tmplObj);
        let parsedParams = null;
        if (existingData.parameters) {
          try {
            parsedParams = typeof existingData.parameters === 'string'
              ? JSON.parse(existingData.parameters)
              : existingData.parameters;
          } catch (e) {
            parsedParams = existingData.parameters;
          }
        }
        setTemplateSubmittedData({
          selectedTemplate: tmplObj,
          selectedWhatsAppSetting,
          parameterValues: parsedParams || {},
        });
      }

      if (existingData?.file_id) {
        setExistingFileInfo({
          id: existingData?.file_id,
          title: existingData?.file_title,
          type: existingData?.file_type,
          size: existingData?.file_size,
          description: existingData?.file_description,
        });
      }
    }
  }, [isEditCloneMode, existingData, selectedWhatsAppSetting]);

  useEffect(() => {
    if (isEditCloneMode && existingData?.lead_ids && leadOptions.length > 0) {
      let rawLeadIds = existingData.lead_ids;
      if (typeof rawLeadIds === 'string') {
        try {
          rawLeadIds = JSON.parse(rawLeadIds);
        } catch (e) {
          rawLeadIds = [rawLeadIds];
        }
      }
      if (!Array.isArray(rawLeadIds)) {
        rawLeadIds = [rawLeadIds];
      }

      const normalizedLeadIds = rawLeadIds.map(lead =>
        typeof lead === 'object' && lead !== null
          ? String(lead.member_id || lead.id || lead.value)
          : String(lead)
      );

      const matchedLeads = leadOptions.filter(option =>
        normalizedLeadIds.includes(String(option.member_id)) ||
        normalizedLeadIds.includes(String(option.value))
      );

      if (matchedLeads.length > 0) {
        setSelectedLeads(matchedLeads);
      }
    }
  }, [isEditCloneMode, existingData, leadOptions]);

  const fetchDefaultLeads = async () => {
    try {
      setIsLoadingRecipients(true);
      console.log('CampaignAdd - fetchDefaultLeads started');
      const dataApi = new DataApi('watconnect_history');
      let leadsResult = await dataApi.fetchAll(
        `filter=lead&search=${encodeURIComponent('')}&businessNumber=${encodeURIComponent(selectedWhatsAppSetting || '')}`
      );

      console.log('CampaignAdd - fetchDefaultLeads raw response:', leadsResult.data);

      if (leadsResult.data.success) {
        console.log('✅ API returned', leadsResult.data.records.length, 'lead records');
        const defaultOptions = leadsResult.data.records.map(lead => ({
          label: `${lead.name || 'Unknown'} - ${lead.whatsapp_number || ''}`,
          value: lead.id,
          member_id: lead.id,
          name: lead.name,
          whatsapp_number: lead.whatsapp_number,
          recordtypename: 'lead',
        }));
        console.log('CampaignAdd - fetchDefaultLeads mapped options:', defaultOptions);
        setLeadOptions(defaultOptions);
      }
    } catch (err) {
      console.error('Error fetching default leads:', err);
    } finally {
      setIsLoadingRecipients(false);
    }
  };

  const fetchListViewsForConfig = async (config) => {
    if (!config || !config.object_name) {
      setListViews([]);
      setSelectedListView(null);
      await fetchRecipientsForConfig(config, null);
      return;
    }
    try {
      setIsLoadingListViews(true);
      console.log('CampaignAdd - fetchListViewsForConfig started for:', config.object_name);
      const listViewApi = new DataApi(`objectconfig/listviews/${config.object_name}`);
      const response = await listViewApi.fetchAll('platform=web');

      console.log('CampaignAdd - fetchListViewsForConfig raw response:', response.data);

      if (response?.data?.success && response.data.data && response.data.data.length > 0) {
        const fetchedViews = response.data.data;
        setListViews(fetchedViews);

        const defaultId = response.data.userDefaultListViewId;
        const initialView = fetchedViews.find(v => v.id === defaultId) || fetchedViews[0];
        setSelectedListView(initialView);
        const initialFilters = initialView?.config?.filters || [];
        originalListViewFiltersRef.current = JSON.parse(JSON.stringify(initialFilters));
        originalListViewLogicRef.current = initialView?.config?.logic || 'AND';
        setExternalFilters(initialFilters);
        setActiveFilters(initialFilters);
        await fetchRecipientsForConfig(config, initialView, initialFilters);
      } else {
        setListViews([]);
        setSelectedListView(null);
        originalListViewFiltersRef.current = [];
        originalListViewLogicRef.current = 'AND';
        setExternalFilters([]);
        setActiveFilters([]);
        await fetchRecipientsForConfig(config, null, []);
      }
    } catch (error) {
      console.error('Error fetching list views:', error);
      setListViews([]);
      setSelectedListView(null);
      originalListViewFiltersRef.current = [];
      originalListViewLogicRef.current = 'AND';
      setExternalFilters([]);
      setActiveFilters([]);
      await fetchRecipientsForConfig(config, null, []);
    } finally {
      setIsLoadingListViews(false);
    }
  };

  const fetchRecipientsForConfig = async (config, listView = null, filtersToApply = activeFilters) => {
    if (!config || !config.object_name) {
      setLeadOptions([]);
      setSelectedLeads([]);
      return;
    }
    try {
      setIsLoadingRecipients(true);
      const listViewName = listView?.name || listView?.id;

      // Helpers to safely extract raw value or display value from primitive or field object { value, displayValue, ... }
      const getRawVal = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') {
          if (val.value !== undefined && val.value !== null) {
            return String(val.value);
          }
          if (val.displayValue !== undefined && val.displayValue !== null) {
            return String(val.displayValue);
          }
          return '';
        }
        return String(val);
      };

      const getDisplayVal = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') {
          if (val.displayValue !== undefined && val.displayValue !== null) {
            return String(val.displayValue);
          }
          if (val.value !== undefined && val.value !== null) {
            return String(val.value);
          }
          return '';
        }
        return String(val);
      };

      const getVal = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') {
          if (val.displayValue !== undefined && val.displayValue !== null) {
            return String(val.displayValue);
          }
          if (val.value !== undefined && val.value !== null) {
            return String(val.value);
          }
          return '';
        }
        return String(val);
      };

      const matchesFilters = (rec, filters) => {
        if (!Array.isArray(filters) || filters.length === 0) return true;

        return filters.every(f => {
          if (!f.field || f.value === undefined || f.value === null || String(f.value).trim() === '') {
            return true;
          }
          const fieldKey = Object.keys(rec).find(k => k.toLowerCase() === f.field.toLowerCase()) || f.field;
          const rawVal = rec[fieldKey];
          const fieldRawValStr = getRawVal(rawVal).toLowerCase().trim();
          const fieldDispValStr = getDisplayVal(rawVal).toLowerCase().trim();
          const filterValStr = String(f.value).toLowerCase().trim();
          const filterDispValStr = String(f.displayValue || '').toLowerCase().trim();

          const op = (f.operator || 'equals').toLowerCase();

          const checkMatch = (valStr, targetStr) => {
            if (!targetStr) return false;
            if (op === 'equals' || op === '=') {
              return valStr === targetStr;
            }
            if (op === 'not_equals' || op === 'not equals' || op === '!=') {
              return valStr !== targetStr;
            }
            if (op === 'contains') {
              return valStr.includes(targetStr);
            }
            if (op === 'in') {
              const inValues = targetStr.split(',').map(v => v.trim()).filter(Boolean);
              return inValues.includes(valStr);
            }
            if (op === 'not in' || op === 'not_in') {
              const inValues = targetStr.split(',').map(v => v.trim()).filter(Boolean);
              return !inValues.includes(valStr);
            }
            return valStr.includes(targetStr);
          };

          if (op === 'is null' || op === 'is_null') {
            return !fieldRawValStr && !fieldDispValStr;
          }
          if (op === 'is not null' || op === 'is_not_null') {
            return fieldRawValStr.length > 0 || fieldDispValStr.length > 0;
          }

          // Match against raw value OR display value
          return checkMatch(fieldRawValStr, filterValStr) ||
                 checkMatch(fieldDispValStr, filterValStr) ||
                 (filterDispValStr ? (checkMatch(fieldRawValStr, filterDispValStr) || checkMatch(fieldDispValStr, filterDispValStr)) : false);
        });
      };

      if (listViewName) {
        // Call clean listview endpoint without invalid query filters parameter
        const dataApi = new DataApi(`object/listview/${config.object_name}/${listViewName}`);
        let res = await dataApi.fetchAll('limit=all&platform=web&viewType=list');

        const recordsList = res.data?.data?.records || res.data?.records || (Array.isArray(res.data?.data) ? res.data.data : []);

        const filteredRecordsList = (Array.isArray(filtersToApply) && filtersToApply.length > 0)
          ? recordsList.filter(rec => matchesFilters(rec, filtersToApply))
          : recordsList;

        if (filteredRecordsList) {
          const targetFieldName = (config.field_name || 'phone').toLowerCase();
          const recipientOptions = filteredRecordsList
            .map(rec => {
              const recId = getRawVal(rec.id);
              const firstName = getDisplayVal(rec.first_name || rec.firstname);
              const lastName = getDisplayVal(rec.last_name || rec.lastname);
              const fullNameCombined = (firstName && lastName) ? `${firstName} ${lastName}` : '';

              const name = getDisplayVal(rec.name) ||
                fullNameCombined ||
                getDisplayVal(rec.full_name) ||
                getDisplayVal(rec.contactname) ||
                getDisplayVal(rec.title) ||
                'Unknown';

              const matchingKey = Object.keys(rec).find(k => k.toLowerCase() === targetFieldName) || config.field_name;
              const phoneVal = getRawVal(rec[matchingKey]);

              return {
                label: `${name}${phoneVal ? ` - ${phoneVal}` : ''}`,
                value: recId,
                member_id: recId,
                name: name,
                whatsapp_number: phoneVal,
                recordtypename: config.object_name,
              };
            })
            .filter(opt => opt.whatsapp_number && String(opt.whatsapp_number).trim() !== '');

          setLeadOptions(recipientOptions);
          return;
        }
      }
      // Fallback if no listViewName or listview endpoint returned no records
      const dataApi = new DataApi('watconnect_history');
      const activeFilter = config.object_name.toLowerCase();
      let queryParams = `filter=${activeFilter}&search=${encodeURIComponent('')}&businessNumber=${encodeURIComponent(selectedWhatsAppSetting || '')}&phone=${encodeURIComponent(config.field_name)}`;
      if (listViewName) {
        queryParams += `&listView=${encodeURIComponent(listViewName)}`;
      }

      let res = await dataApi.fetchAll(queryParams);

      console.log('CampaignAdd - fetchRecipientsForConfig fallback raw response:', res.data);

      if (res.data?.success && res.data?.records) {
        const targetFieldName = (config.field_name || 'phone').toLowerCase();
        const recipientOptions = res.data.records
          .map(rec => {
            const recId = getVal(rec.id);
            const name = getVal(rec.name || rec.contactname || 'Unknown');
            const matchingKey = Object.keys(rec).find(k => k.toLowerCase() === targetFieldName) || config.field_name;
            const phoneVal = getVal(rec[matchingKey]);
            return {
              label: `${name}${phoneVal ? ` - ${phoneVal}` : ''}`,
              value: recId,
              member_id: recId,
              name: name,
              whatsapp_number: phoneVal,
              recordtypename: config.object_name,
            };
          })
          .filter(opt => opt.whatsapp_number && String(opt.whatsapp_number).trim() !== '');
        setLeadOptions(recipientOptions);
      } else {
        setLeadOptions([]);
      }
    } catch (error) {
      console.error('Error fetching dynamic recipients:', error);
      toast.error(`Failed to load recipients for ${config.object_name}`);
      setLeadOptions([]);
    } finally {
      setIsLoadingRecipients(false);
    }
  };

  const fetchAllData = async () => {
    try {
      setIsSpinner(false);
      const configApi = new DataApi('watconnect/watconnect_module_config');
      const configRes = await configApi.fetchAll();

      if (configRes.data?.success && configRes.data?.data && configRes.data.data.length > 0) {
        // Filter configurations to only show active ones (or old ones without is_active defined)
        // Also retain the currently selected configuration if in Edit/Clone mode
        const activeConfigs = configRes.data.data.filter(c =>
          c.is_active !== false || (isEditCloneMode && c.id === existingData?.module_config)
        );
        setModuleConfigs(activeConfigs);

        // Default to pre-selected config if not in edit mode
        if (activeConfigs.length > 0) {
          if (!isEditCloneMode || !existingData?.module_config) {
            const defaultConf = activeConfigs[0];
            setSelectedConfig(defaultConf);
            await fetchListViewsForConfig(defaultConf);
          }
        } else {
          setSelectedConfig(null);
          setListViews([]);
          setSelectedListView(null);
          await fetchDefaultLeads();
        }
      } else {
        setModuleConfigs([]);
        setSelectedConfig(null);
        setListViews([]);
        setSelectedListView(null);
        await fetchDefaultLeads();
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
      await fetchDefaultLeads();
    } finally {
      setIsSpinner(true);
    }
  };

  // Pre-select dynamic configuration in Edit/Clone mode
  useEffect(() => {
    if (moduleConfigs.length > 0 && isEditCloneMode && existingData?.module_config) {
      const matchedConfig = moduleConfigs.find(c => c.id === existingData.module_config);
      if (matchedConfig) {
        setSelectedConfig(matchedConfig);
        fetchListViewsForConfig(matchedConfig);
      }
    }
  }, [isEditCloneMode, existingData, moduleConfigs]);
  const handleLeadChange = selectedOption => {
    setSelectedLeads(selectedOption);
  };

  const handleChangeName = event => {
    const { name, value } = event.target;
    if (name === 'schedule_date') {
      setCampaignRecord({
        ...campaignRecord,
        [name]: value ? moment(value).tz('Asia/Kolkata').toDate() : '',
      });
    } else {
      setCampaignRecord({ ...campaignRecord, [name]: value });
    }
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = ['csv'];
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrorMessage('Only .csv files are allowed.');
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = event => {
        const content = event.target.result;
        const rows = content
          .split('\n')
          .map(row => row.trim())
          .filter(Boolean);

        if (rows.length === 0) {
          setErrorMessage('The file is empty.');
          setSelectedFile(null);
          return;
        }

        const headers = rows[0]
          .split(',')
          .map(header => header.trim().toLowerCase());
        const expectedHeaders = ['name', 'countrycode', 'number'];

        const isHeaderValid = expectedHeaders.every(
          (h, index) => headers[index] === h
        );
        if (!isHeaderValid) {
          setErrorMessage(
            'File must contain headers in order: Name, Country Code, Number.'
          );
          setSelectedFile(null);
          event.target.value = '';
          return;
        }

        const members = rows.slice(1).map(row => {
          const [name, countrycode, number] = row
            .split(',')
            .map(val => val.trim());
          return { name, countrycode, number };
        });

        setFileMembers(members);
        setSelectedFile([file]);
        setExistingFileInfo(null);
        setErrorMessage('');
      };
      reader.readAsText(file);
    }
  };

  // inside your CampaignAdd.jsx or CampaignAdd.js

  const handleTemplateSend = submittedData => {
    console.log('📩 Template submitted data received:', submittedData);
    setShowTemplateModal(false);
    setTemplateSubmittedData(submittedData); // store temporarily
    setSelectedTemplate(submittedData.selectedTemplate);
  };
  // main campaign submit handler
  const handleSubmit = async () => {
    try {
      const errors = {};

      // Validation
      if (!campaignRecord.name) {
        errors.name = 'Campaign name is required.';
      }
      if (!campaignRecord?.schedule_date) {
        errors.schedule_date = 'Schedule date & time is required.';
      }

      // Validate that at least one of leads or file is provided
      if (selectedLeads.length === 0 && !selectedFile && !existingFileInfo) {
        errors.recipients = 'Either select leads or upload a file.';
      }

      if (
        recurrenceType === 'custom' &&
        customRecurrenceUnit === 'week' &&
        selectedDays.length === 0
      ) {
        errors.recurrence =
          'Please select at least one day for weekly recurrence.';
      }

      if (
        recurrenceType === 'custom' &&
        customRecurrenceUnit === 'month' &&
        !selectedDate
      ) {
        errors.recurrence = 'Please select a date for monthly recurrence.';
      }

      if (recurrenceEndType === 'on' && !recurrenceEndDate) {
        errors.recurrenceEndDate =
          'Please select an end date for the recurrence.';
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      setFormErrors({});

      if (!templateSubmittedData) {
        alert(
          'Please select a template and fill parameters before submitting.'
        );
        return;
      }

      const {
        selectedTemplate,
        selectedWhatsAppSetting: templateWhatsAppSetting,
        fileUploads,
        parameterValues,
      } = templateSubmittedData;

      if (!selectedTemplate) {
        errors.template = 'Template is required.';
      }

      const campaignApi = new DataApi('watconnect');
      const attachmentsApi = new DataApi('attachments');

      setIsSending(true);

      // STEP 1️⃣: CREATE CAMPAIGN FIRST
      const leadIds = selectedLeads.map(lead => lead.member_id);

      const formattedStartDate = campaignRecord.schedule_date
        ? moment(campaignRecord.schedule_date)
          .tz('Asia/Kolkata')
          .format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : moment
          .tz(new Date(), 'Asia/Kolkata')
          .format('YYYY-MM-DDTHH:mm:ss.SSSZ');

      // STEP 2️⃣: CREATE CAMPAIGN (actual API call)
      // Convert arrays to PostgreSQL format or omit empty arrays
      const campaignData = {
        name: campaignRecord.name,
        type: campaignRecord.type || 'Web',
        status: 'Pending',
        template_name: selectedTemplate.name,
        business_number: selectedWhatsAppSetting,
        schedule_date: formattedStartDate,
        recurrence_end_type: recurrenceEndType,
        parameters: parameterValues,
        module_config: selectedConfig?.id || null, // ADDED
      };

      // Only include lead_ids if not empty, or format for PostgreSQL
      if (leadIds.length > 0) {
        campaignData.lead_ids = leadIds;
      } else {
        // For PostgreSQL, try sending as empty PostgreSQL array format
        campaignData.lead_ids = '{}'; // PostgreSQL empty array format
      }

      // Handle recurrence fields - only include if they have values
      if (recurrenceType) {
        campaignData.recurrence = recurrenceType;
      }

      if (recurrenceType === 'custom' && customRecurrenceUnit) {
        campaignData.custom_recurrence_unit = customRecurrenceUnit;
      }

      // Handle selected_days - only include if applicable and not empty
      if (customRecurrenceUnit === 'week') {
        if (selectedDays.length > 0) {
          campaignData.selected_days = selectedDays;
        } else {
          campaignData.selected_days = '{}'; // PostgreSQL empty array format
        }
      }

      // Handle selected_date - only include if applicable
      if (customRecurrenceUnit === 'month' && selectedDate) {
        campaignData.selected_date = selectedDate;
      }

      // Handle recurrence end date
      if (recurrenceEndType === 'on' && recurrenceEndDate) {
        campaignData.recurrence_end_date =
          moment(recurrenceEndDate).format('YYYY-MM-DD');
      }

      console.log('Campaign Data:', campaignData);

      // ✅ CREATE CAMPAIGN FIRST
      const createRes = await campaignApi.post(`/campaign`, campaignData);
      console.log('API Response:', createRes);

      if (!createRes?.data?.success) {
        const apiMessage =
          createRes?.data?.message ||
          (Array.isArray(createRes?.data?.errors)
            ? createRes.data.errors.join(', ')
            : createRes?.data?.errors);
        throw new Error(apiMessage || 'Campaign creation failed');
      }

      const campaignId =
        createRes.data.data?.id || createRes.data.record?.id;
      if (!campaignId) {
        throw new Error('Campaign created but no record id was returned');
      }
      console.log('✅ Campaign created:', campaignId);

      // STEP 3️⃣: UPLOAD MAIN CAMPAIGN FILE (if any) - AFTER CAMPAIGN CREATION
      if (selectedFile) {
        console.log('📤 Uploading campaign file...');
        const fileForm = new FormData();
        for (let i = 0; i < selectedFile.length; i++) {
          fileForm.append('files', selectedFile[i]);
        }
        fileForm.append(
          'description',
          `${campaignRecord.name} file for WatConnect`
        );

        const attachmentRes = await attachmentsApi.post(
          `/${campaignId}`,
          fileForm
        );
        console.log('File upload response:', attachmentRes);
      }

      // Restore original list view filters if quick filters were applied
      if (selectedListView && objectName && originalListViewFiltersRef.current) {
        try {
          const restoredConfig = {
            ...selectedListView.config,
            filters: originalListViewFiltersRef.current,
            logic: originalListViewLogicRef.current || 'AND',
          };
          const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
          await configApi.update({ config: restoredConfig }, selectedListView.id);
          console.log('✅ Restored original list view filters after campaign submit');
        } catch (revertErr) {
          console.error('Failed to restore original list view filters after campaign submit:', revertErr);
        }
      }

      toast.success('Campaign created successfully!');
      navigate('/watconnect/campaigns');
    } catch (error) {
      console.error('❌ Error in campaign submission:', error);
      const message =
        error?.response?.data?.message ||
        (Array.isArray(error?.response?.data?.errors)
          ? error.response.data.errors.join(', ')
          : error?.response?.data?.errors) ||
        error.message;
      toast.error(`Error: ${message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleClear = () => {
    if (selectedListView && objectName && originalListViewFiltersRef.current) {
      try {
        const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
        configApi.update({
          config: {
            ...selectedListView.config,
            filters: originalListViewFiltersRef.current,
            logic: originalListViewLogicRef.current || 'AND',
          }
        }, selectedListView.id).catch(err => console.error('Error reverting listview filters on handleClear:', err));
      } catch (e) {}
    }
    setSelectedTemplate(null);
    setCampaignRecord({
      name: '',
      description: '',
      schedule_date: '',
      type: 'Web',
    });
    setSelectedFile(null);
    setSelectedLeads([]);
    setFileMembers([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBack = () => {
    navigate('/watconnect/campaigns');
  };

  const formatDateForInput = date => {
    return date ? moment(date).format('YYYY-MM-DDTHH:mm') : '';
  };

  const csvData = [
    ['Name', 'CountryCode', 'Number'],
    ['John', '91', 'XXXXXXXXXX'],
  ];

  const convertToCSV = array => {
    return array.map(row => row.join(',')).join('\n');
  };

  const downloadCSV = () => {
    const csvString = convertToCSV(csvData);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'contacts.csv');
  };

  const handleDayToggle = day => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  console.log('CampaignAdd Render: isSpinner =', isSpinner, 'isLoadingRecipients =', isLoadingRecipients, 'leadOptions.length =', leadOptions.length, 'selectedLeads.length =', selectedLeads.length);

  if (!isSpinner) {
    return (
      <div className='sk-cube-grid'>
        <div className='sk-cube sk-cube1'></div>
        <div className='sk-cube sk-cube2'></div>
        <div className='sk-cube sk-cube3'></div>
        <div className='sk-cube sk-cube4'></div>
        <div className='sk-cube sk-cube5'></div>
        <div className='sk-cube sk-cube6'></div>
        <div className='sk-cube sk-cube7'></div>
        <div className='sk-cube sk-cube8'></div>
        <div className='sk-cube sk-cube9'></div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Force react-multi-select-component dropdown to render on top of other form fields */
        .rmsc {
          --rmsc-z-index: 9999 !important;
        }
        .rmsc .dropdown-content {
          display: block !important;
          z-index: 9999 !important;
          position: absolute !important;
          background-color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          border: 1px solid #ccc !important;
          border-radius: 4px !important;
          max-height: 260px !important;
          overflow-y: auto !important;
        }
        /* Ensure parent bootstrap form and columns do not clip the dropdown content */
        .card, .card-body, .row, .col-lg-6, .col-sm-12, .mb-3, .form-group {
          overflow: visible !important;
        }
      `}</style>
      <Container className='mt-5'>
        <Row className='mx-5 text-center g-0'>
          <Col lg={12} xs={12} sm={12}>
            <div
              className='text-center p-2'
              style={{
                height: '40px',
                backgroundColor: '#ffffff',
                borderRadius: '5px',
              }}
            >
              <span
                className='fw-semibold p-1'
                style={{ color: '#605C68', fontSize: 'large' }}
              >
                Add Campaign
              </span>
            </div>
          </Col>
        </Row>
      </Container>

      <Container className='mt-1 mb-5'>
        <Row className='mx-5 g-0'>
          <Col lg={12} sm={12} xs={12} className='mb-2'>
            <Card className='h-100' style={{ border: 'none' }}>
              <Card.Body>
                <Row className='mb-1'>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group
                      className='mb-3 mx-2'
                      controlId='formCampaignName'
                    >
                      <Form.Label>Campaign Name</Form.Label>
                      <Form.Control
                        style={{ height: '36px' }}
                        required
                        type='text'
                        name='name'
                        placeholder='Enter campaign name'
                        value={campaignRecord?.name}
                        onChange={handleChangeName}
                        isInvalid={!!formErrors.name}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {formErrors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2' controlId='formStartDate'>
                      <Form.Label>Schedule Date & Time</Form.Label>
                      <Form.Control
                        style={{ height: '36px' }}
                        required
                        type='datetime-local'
                        name='schedule_date'
                        value={formatDateForInput(
                          campaignRecord?.schedule_date
                        )}
                        onChange={handleChangeName}
                        isInvalid={!!formErrors.schedule_date}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {formErrors.schedule_date}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-1'>
                  {moduleConfigs.length > 0 && (
                    <Col lg={4} sm={12} xs={12} className="mb-3">
                      <Form.Group className='mx-2' controlId='formModuleConfig'>
                        <Form.Label>Recipient Module Configuration</Form.Label>
                        <Form.Select
                          style={{ height: '42px' }}
                          value={selectedConfig?.id || ''}
                          onChange={(e) => {
                            const conf = moduleConfigs.find(c => c.id === e.target.value);
                            setSelectedConfig(conf);
                            setSelectedLeads([]); // Clear selected recipients when configuration changes
                            setSelectedTemplate(null); // Reset template selection
                            setTemplateSubmittedData(null); // Clear template submitted parameters
                            fetchListViewsForConfig(conf);
                          }}
                        >
                          {moduleConfigs.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name} ({c.object_name} ➔ {c.field_name})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}

                  {selectedConfig && (
                    <Col lg={4} sm={12} xs={12} className="mb-3">
                      <Form.Group className='mx-2' controlId='formListView'>
                        <Form.Label>
                          <span>
                            List View {isLoadingListViews ? (
                              <span className="text-muted ms-2" style={{ fontSize: 'smaller' }}>
                                <i className="fa fa-spinner fa-spin me-1"></i>Loading...
                              </span>
                            ) : (
                              <span className="text-success ms-2" style={{ fontSize: 'smaller', fontWeight: 'normal' }}>
                                ({listViews.length} available)
                              </span>
                            )}
                          </span>
                        </Form.Label>
                        <Form.Select
                          style={{ height: '42px' }}
                          value={selectedListView?.name || selectedListView?.id || ''}
                          disabled={isLoadingListViews || listViews.length === 0}
                          onChange={(e) => {
                            const lv = listViews.find(v => (v.name === e.target.value || v.id === e.target.value));

                            // Restore previous list view filters if modified
                            if (selectedListView && selectedListView.id !== lv?.id && objectName && originalListViewFiltersRef.current) {
                              const currentSavedFilters = selectedListView?.config?.filters || [];
                              if (JSON.stringify(currentSavedFilters) !== JSON.stringify(originalListViewFiltersRef.current)) {
                                try {
                                  const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
                                  configApi.update({
                                    config: {
                                      ...selectedListView.config,
                                      filters: originalListViewFiltersRef.current,
                                      logic: originalListViewLogicRef.current || 'AND',
                                    }
                                  }, selectedListView.id).catch(err => console.error('Error reverting previous listview filters on dropdown change:', err));
                                } catch (e) {}
                              }
                            }

                            setSelectedListView(lv || null);
                            setSelectedLeads([]); // Clear selected recipients when list view changes
                            const lvFilters = lv?.config?.filters || [];
                            originalListViewFiltersRef.current = JSON.parse(JSON.stringify(lvFilters));
                            originalListViewLogicRef.current = lv?.config?.logic || 'AND';
                            setExternalFilters(lvFilters);
                            setActiveFilters(lvFilters);
                            fetchRecipientsForConfig(selectedConfig, lv || null, lvFilters);
                          }}
                        >
                          {listViews.length === 0 ? (
                            <option value=''>No List Views</option>
                          ) : (
                            listViews.map(lv => (
                              <option key={lv.id || lv.name} value={lv.name || lv.id}>
                                {lv.label || lv.name}
                              </option>
                            ))
                          )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}

                  <Col lg={selectedConfig ? 4 : 12} sm={12} className="mb-3">
                    <Form.Group
                      className='mx-2'
                      controlId='formSelectLeads'
                    >
                      <Form.Label>
                        Select Recipients {isLoadingRecipients ? (
                          <span className="text-muted ms-2" style={{ fontSize: 'smaller' }}><i className="fa fa-spinner fa-spin me-1"></i>Loading...</span>
                        ) : (
                          <span className="text-success ms-2" style={{ fontSize: 'smaller', fontWeight: 'normal' }}>
                            ({leadOptions.length} loaded)
                          </span>
                        )}
                      </Form.Label>
                      <MultiSelect
                        options={leadOptions}
                        value={selectedLeads}
                        onChange={handleLeadChange}
                        labelledBy='Select Recipients'
                        disabled={isLoadingRecipients}
                      />
                      {formErrors.recipients && (
                        <Form.Text className='text-danger'>
                          {formErrors.recipients}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-1'>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2'>
                      <Form.Label htmlFor='formType'>Type</Form.Label>
                      <Form.Select
                        className='w-100'
                        style={{ height: '42px' }}
                        aria-label='select type'
                        name='type'
                        value={campaignRecord?.type}
                        onChange={handleChangeName}
                      >
                        <option value='Web'>Web</option>
                        <option value='Advertisement'>Advertisement</option>
                        <option value='Banner Ads'>Banner Ads</option>
                        <option value='Conference'>Conference</option>
                        <option value='Direct Mail'>Direct Mail</option>
                        <option value='Email'>Email</option>
                        <option value='Partners'>Partners</option>
                        <option value='Public Relations'>
                          Public Relations
                        </option>
                        <option value='Other'>Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2' controlId='formFile'>
                      <Form.Label>
                        {existingFileInfo ? (
                          <>
                            Existing file:&nbsp;
                            <a
                              href={`/public/${userInfo.tenantcode}/campaign_files/${existingData.file_title}`}
                              download={existingData.file_title}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {existingFileInfo.title} ({existingFileInfo.size}{' '}
                              KB)
                            </a>
                          </>
                        ) : (
                          'File Upload'
                        )}
                      </Form.Label>
                      <Form.Control
                        type='file'
                        ref={fileInputRef}
                        accept='.csv'
                        onChange={handleFileChange}
                      />
                      {errorMessage && (
                        <Form.Text className='text-danger'>
                          {errorMessage}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-1'>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2'>
                      <Form.Label htmlFor='formType'>
                        <strong className='text-danger'>*</strong> Select
                        Template
                      </Form.Label>
                      <Button
                        className='w-100 btn-group text-capitalize'
                        onClick={() => setShowTemplateModal(true)}
                        variant={
                          formErrors.template
                            ? 'outline-danger'
                            : 'outline-primary'
                        }
                      >
                        {selectedTemplate?.name?.replace(/_/g, ' ') ||
                          'Select Template...'}
                      </Button>
                      {formErrors.template && (
                        <Form.Text className='text-danger'>
                          {formErrors.template}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mt-3'>
                  <Col lg={12} sm={12} xs={12}>
                    <hr />
                  </Col>
                </Row>

                <Row className='g-0 mb-2'>
                  <Col lg={3} sm={3} xs={3} className='text-start mt-2'>
                    <Button
                      className='ms-2'
                      variant='secondary'
                      onClick={downloadCSV}
                    >
                      <i className='fa fa-download me-2'></i>Download Sample CSV
                    </Button>
                  </Col>
                  <Col lg={9} sm={9} xs={9} className='text-end mt-2'>
                    <b>
                      Approximate Total Cost{' '}
                      <span className='text-success'>{finalTotalCost}</span>
                    </b>

                    <Button
                      className='mx-2'
                      variant='light'
                      onClick={handleBack}
                      disabled={isSending}
                    >
                      Back
                    </Button>
                    <Button
                      className='mx-2'
                      variant='light'
                      onClick={handleClear}
                      disabled={isSending}
                    >
                      Clear
                    </Button>
                    <Button
                      variant='primary'
                      disabled={isSending}
                      onClick={handleSubmit}
                    >
                      {isSending ? 'Submitting...' : 'Submit'}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {showTemplateModal && (
          <TemplateModal
            isOpen={showTemplateModal}
            onClose={() => setShowTemplateModal(false)}
            selectedWhatsAppSetting={selectedWhatsAppSetting}
            onSendComplete={handleTemplateSend}
            campaignRecord={campaignRecord}
            selectedConfig={selectedConfig}
            initialTemplateName={existingData?.template_name}
            initialParameters={
              existingData?.parameters
                ? typeof existingData.parameters === 'string'
                  ? JSON.parse(existingData.parameters)
                  : existingData.parameters
                : null
            }
          />
        )}
      </Container>

      <ToastContainer />
    </>
  );
};

export default CampaignAdd;
